<script lang="ts">
  import PageMeta from '$lib/components/PageMeta.svelte';
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import GridInner from '$lib/components/GridInner.svelte';
  import Star from '$lib/components/Star.svelte';
  import ContactCtaSection from '$lib/sections/ContactCtaSection.svelte';
  import { hatchStyle45 } from '$lib/styles/hatch';

  const whatItGets = [
    'Central admin console — every agent, every user, every data connection visible in one place',
    'Entra ID / Azure AD integration — role-based access that respects your existing org structure',
    'Sandbox → production promotion with admin approval gates',
    'Full audit trail — every interaction, every tool call, every data access logged',
    'Cumulative Restrictions Framework — automated security controls that govern agent behavior based on what it\'s been exposed to',
    'Zero data egress — runs entirely in your Azure tenant, behind your network',
    'Alerting for anomalous agent behavior or policy violations',
  ];

  const whatItDoesnt = [
    'Build a governance layer from scratch',
    'Review every agent configuration manually — the framework handles baseline security',
    'Fight with product teams about compliance — it\'s built into the architecture',
    'Worry about data leaving the tenant — it can\'t',
  ];
</script>

<PageMeta
  title="IT & Security Teams — Govern everything. Block nothing."
  description="Full visibility and control without being the department that says no. Every agent, every action, every data flow — visible, governed, auditable."
/>

<!-- Top stars -->
<div class="relative">
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] -top-px" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] -top-px" />
</div>

<!-- Hero -->
<section class="bg-[#F9F9F4] py-[80px] lg:py-[120px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px]">
        <p class="font-mono text-[14px] text-[#ee6018] mb-4">IT & Security Teams</p>
        <h1 class="font-sans text-[48px] lg:text-[64px] tracking-[-0.04em] leading-[1.1] text-[#020202] mb-6">
          Govern everything. Block nothing.
        </h1>
        <p class="font-mono font-light text-[16px] text-[#5C5855] leading-relaxed">
          Your organization is going to deploy AI whether IT is involved or not. The question is whether you have visibility and control — or shadow AI spreading across the company with no governance, no audit trail, and no way to know what data is being fed to which model.
        </p>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §1: The pitch -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto">
        <h2 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1] mb-8">
          The governance layer without being the department that says no.
        </h2>
        <div class="flex flex-col gap-6">
          <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
            Corral gives IT the governance layer without being the department that says no. Users get powerful AI. You get full visibility into what's being built, what data it accesses, what actions it takes, and who approved it.
          </p>
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#0d0d0d] relative">
  <GridLines variant="dark" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#333335]" aria-hidden="true"></div>
  <Star variant="full" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="full" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §2: What IT gets -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto">
        <span class="font-mono text-[12px] text-[#ee6018] uppercase tracking-wider mb-3 block">What IT gets</span>
        <h2 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1] mb-8">
          Full visibility. Total control.
        </h2>
        <ul class="flex flex-col gap-0">
          {#each whatItGets as item, i}
            <li class="flex items-start gap-3 py-4 {i < whatItGets.length - 1 ? 'border-b border-[#333335]' : ''}">
              <span class="w-1.5 h-1.5 bg-[#ee6018] rounded-full mt-2 shrink-0"></span>
              <span class="font-mono font-light text-[14px] text-[#cfcfcf] leading-relaxed">{item}</span>
            </li>
          {/each}
        </ul>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute top-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] top-0" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] top-0" />
</div>

<!-- §3: What IT doesn't have to do -->
<section class="bg-[#f9f9f4] py-[80px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto">
        <span class="font-mono text-[12px] text-[#ee6018] uppercase tracking-wider mb-3 block">What IT doesn't have to do</span>
        <h2 class="font-sans text-[36px] lg:text-[48px] text-[#020202] tracking-[-0.04em] leading-[1.1] mb-8">
          Less work. More control.
        </h2>
        <ul class="flex flex-col gap-5">
          {#each whatItDoesnt as item}
            <li class="flex items-start gap-3">
              <span class="w-1.5 h-1.5 bg-[#ee6018] rounded-full mt-2 shrink-0"></span>
              <span class="font-mono font-light text-[16px] text-[#5C5855] leading-relaxed">{item}</span>
            </li>
          {/each}
        </ul>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider: → CTA -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- CTA -->
<ContactCtaSection />
